import { getApolloContext } from "@apollo/client";
import { useTheme } from "@emotion/react";
import { faSpinnerThird } from "@fullscript/pro-regular-svg-icons";
import { colorProfileMapper } from "aviary-tokens";
import type { HTMLProps, Ref } from "react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { useButtonClickContext, ButtonClickProvider } from "@shared/context/ButtonClickContext";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { Link } from "@shared/react-router-dom/react-router-dom";

import { Skeleton } from "../Skeleton";

import type { ButtonProps, ButtonOrLinkType } from "./Button.types";

import * as styles from "./Button.styles";

/**
 * @deprecated Use `Button` from `@fullscript/aviary-web` instead.
 */
const Button = ({
  isColor = "primary",
  size = "medium",
  isSkeleton = false,
  ...props
}: ButtonOrLinkType) => {
  const { t } = useTranslation("aviary");
  const currentTheme = useTheme();
  const hasApolloClient = !!useContext(getApolloContext())?.client;
  const { onAnalyticsClick } = useButtonClickContext();

  const {
    children,
    disabled,
    href,
    innerRef,
    intention: receivedIntention,
    isCircular,
    isFullWidth,
    isIcon,
    isLoading,
    isOutlined,
    isText,
    latch,
    noStyle,
    replace,
    to,
    type,
    isLoaderDisabled,
    trackClick = false,
    analyticsName,
    ...rest
  } = props;
  const tagType = href ? "link" : "button";

  // Convert DS3 Button props to DS4 Intentions IF no intention is passed
  let intention = receivedIntention;

  const isLight =
    intention === "lightFilled" || intention === "lightOutlined" || intention === "lightText";

  if (!receivedIntention) {
    switch (true) {
      case isText && isColor === "primary":
        intention = "textSuccess";
        break;
      case isText && isColor === "danger":
        intention = "textDanger";
        break;
      case isText:
        intention = "textSystem";
        break;
      case isOutlined && isColor !== "danger":
        intention = "secondary";
        break;
      case isColor === "system":
        intention = "secondary";
        break;
      case isColor === "primary":
        intention = "primary";
        break;
      case isColor === "danger":
        intention = "danger";
        break;

      default:
        intention = "secondary";
    }
  }

  if (intention === "system") {
    intention = "secondary";
  }

  const themeColors = colorProfileMapper(currentTheme);

  const buttonBuilder = () => {
    if (noStyle) return styles.button.noStyle;

    return [
      isLoading && styles.loadingStyles,
      styles.button.base,
      latch && styles.latch(themeColors[isColor], currentTheme)[latch],
      disabled && styles.latch(themeColors[isColor], currentTheme).disabled,
      isFullWidth && styles.button.fullwidth,
      isCircular && styles.circular,
      styles.button[size as keyof typeof styles.button],
      isIcon && iconBuilder(),
      isLight && lightButtonBuilder(),
      intention && styles.intentionStyles[intention],
    ];
  };

  const lightButtonBuilder = () => {
    return (
      !noStyle && [
        styles.lightStyles.base(currentTheme),
        // TODO: Figure out DS4 Outlined and Text Light button styles
        isOutlined && styles.lightStyles.outlined(currentTheme),
        intention === "lightText" && styles.lightStyles.isText(currentTheme),
        isIcon && styles.lightStyles.isIcon(currentTheme),
        isIcon && intention === "lightText" && styles.lightStyles.isText(currentTheme),
        isIcon && intention === "lightOutlined" && styles.lightStyles.outlined(currentTheme),
      ]
    );
  };

  const iconBuilder = () => {
    return (
      !noStyle && [
        isIcon && styles.baseIconStyles,
        isIcon && styles.iconStyles[size as keyof typeof styles.iconStyles],
        styles.normalSVG(currentTheme),
      ]
    );
  };

  const loaderBuilder = () => {
    return (
      !noStyle && [
        styles.loaderIcon,
        styles.normalSVG(currentTheme),
        isLight && styles.lightStyles.isIcon(currentTheme),
      ]
    );
  };

  const renderLoader = () => {
    if (!noStyle && !isLoaderDisabled && isLoading) {
      return (
        <div css={styles.loader}>
          <FontAwesomeIcon
            css={loaderBuilder()}
            icon={faSpinnerThird}
            spin
            title={t(l.aviary.button.Loading)}
          />
        </div>
      );
    }
  };

  const getValidButtonType = () => {
    if (type === "submit" || type === "reset") {
      return type;
    } else {
      return "button";
    }
  };

  const handleAnalyticsClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    onAnalyticsClick(event, { tagType, analyticsName, trackClick });
  };

  const button = () => (
    <button
      css={buttonBuilder()}
      disabled={disabled}
      {...(rest as Omit<HTMLProps<HTMLButtonElement>, "size">)}
      ref={innerRef as Ref<HTMLButtonElement>}
      onClickCapture={handleAnalyticsClick}
      type={getValidButtonType()}
    >
      {renderLoader()}
      {children}
    </button>
  );

  const anchor = () => (
    <a
      css={buttonBuilder()}
      role="button"
      href={href}
      onClickCapture={handleAnalyticsClick}
      {...(rest as Omit<HTMLProps<HTMLAnchorElement>, "size">)}
      ref={innerRef as Ref<HTMLAnchorElement>}
      aria-disabled={isLoading}
    >
      {renderLoader()}
      {children}
    </a>
  );

  const routerLink = () => (
    <Link
      css={buttonBuilder()}
      to={to!}
      replace={replace}
      data-testid="Link"
      {...rest}
      ref={innerRef as Ref<HTMLAnchorElement>}
      onClickCapture={handleAnalyticsClick}
    >
      {renderLoader()}
      {children}
    </Link>
  );

  const getElement = () => {
    if (isSkeleton) {
      return <Skeleton type="button" buttonSize={size} isFullWidth={isFullWidth} />;
    } else if (!disabled) {
      if (to) {
        return routerLink();
      } else if (href) {
        return anchor();
      }
    }

    return button();
  };
  if (!hasApolloClient) {
    return getElement();
  }

  return <ButtonClickProvider>{getElement()}</ButtonClickProvider>;
};

export { Button, type ButtonProps };
